import { useEffect, useState } from "react";
import { toast } from "sonner";
import { X, ExternalLink, Check, Trash2, EyeOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, errMsg } from "@/lib/api";
import { TypeChips, RoleChips } from "./RelDialog";

const Shell = ({ title, onClose, children, testid }) => (
  <aside className="absolute right-4 top-4 z-20 w-[min(340px,calc(100%-2rem))] max-h-[calc(100%-2rem)] overflow-y-auto rounded-md border border-[#2D3442] bg-[#13171F]/95 p-4 backdrop-blur-md lf-scroll" data-testid={testid}>
    <div className="mb-3 flex items-start justify-between gap-2">
      <h3 className="font-display text-2xl leading-tight text-[#F3EBDD]">{title}</h3>
      <Button size="icon" variant="ghost" className="-mr-2 -mt-1 h-8 w-8" onClick={onClose} data-testid="rel-panel-close"><X className="h-4 w-4" /></Button>
    </div>
    {children}
  </aside>
);

export function NodePanel({ node, edges, names, tmap, onClose, onOpenEntry, onPickEdge, canEdit, onRemove }) {
  const mine = edges.filter((e) => e.a === node.id || e.b === node.id);
  return (
    <Shell title={node.name} onClose={onClose} testid="rel-node-panel">
      {node.summary && <p className="mb-3 text-sm text-[#A8A090]">{node.summary}</p>}
      <div className="lf-label mb-2">{mine.length} connection{mine.length === 1 ? "" : "s"}</div>
      <div className="space-y-1.5">
        {mine.map((e) => {
          const t = tmap[e.type] || {};
          return (
            <button key={e.id || e.key} type="button" onClick={() => onPickEdge(e)} data-testid={`rel-node-conn-${e.id || e.key}`}
              className="flex w-full items-center gap-2 rounded-md border border-[#2D3442] px-3 py-2 text-left text-sm hover:border-[#F59E0B]/50" style={e.suggested ? { borderStyle: "dashed" } : undefined}>
              <span className="h-2 w-2 shrink-0 rounded-full" style={{ background: t.color }} />
              <span className="flex-1 truncate text-[#F3EBDD]">{names[e.a === node.id ? e.b : e.a]}</span>
              <span className="text-xs" style={{ color: t.color }}>{e.suggested ? `${e.label} ?` : t.label}</span>
            </button>
          );
        })}
      </div>
      <div className="mt-4 flex flex-wrap gap-2">
        <Button variant="outline" size="sm" onClick={() => onOpenEntry(node.id)} data-testid="rel-open-node-entry"><ExternalLink className="mr-1.5 h-3.5 w-3.5" />Open entry</Button>
        {canEdit && <Button variant="ghost" size="sm" className="text-red-400" onClick={() => onRemove(node)} data-testid="rel-remove-node"><EyeOff className="mr-1.5 h-3.5 w-3.5" />Remove</Button>}
      </div>
    </Shell>
  );
}

export function EdgePanel({ edge, names, types, tmap, canEdit, onClose, onOpenEntry, onChanged, onConfirm, onDismiss, onDelete }) {
  const [status, setStatus] = useState(edge.status || "");
  useEffect(() => setStatus(edge.status || ""), [edge.status]);
  const t = tmap[edge.type] || {};
  const patch = async (body) => {
    try {
      await api.patch(`/relationships/${edge.id}`, body);
      onChanged();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  if (edge.suggested) return (
    <Shell title={`${names[edge.a]} & ${names[edge.b]}`} onClose={onClose} testid="rel-suggested-panel">
      <p className="text-sm text-[#A8A090]">Suggested from <span className="text-[#F3EBDD]">{edge.source}</span></p>
      <p className="mt-2 text-sm">Looks like <span style={{ color: t.color }}>{t.label}</span> ({edge.label}).</p>
      {canEdit && (
        <div className="mt-4 flex gap-2">
          <Button size="sm" onClick={() => onConfirm(edge)} data-testid="rel-suggested-confirm"><Check className="mr-1.5 h-4 w-4" />Confirm</Button>
          <Button size="sm" variant="ghost" onClick={() => onDismiss(edge)} data-testid="rel-suggested-dismiss">Dismiss</Button>
        </div>
      )}
    </Shell>
  );
  return (
    <Shell title={edge.name} onClose={onClose} testid="rel-edge-panel">
      <p className="mb-3 text-sm text-[#A8A090]">{names[edge.a]} <span style={{ color: t.color }}>— {t.label} —</span> {names[edge.b]}</p>
      {edge.summary && <p className="mb-3 text-sm text-[#E8DFD1]/80">{edge.summary}</p>}
      {canEdit ? (
        <div className="space-y-3">
          <TypeChips types={types} value={edge.type} onChange={(type) => patch({ type })} testid="rel-edit-type" />
          {edge.type === "family" && (
            <div data-testid="rel-edit-role">
              <div className="lf-label mb-1.5">Family role{edge.role_inferred ? " · guessed" : ""}</div>
              <RoleChips a={edge.a} b={edge.b} names={names} value={edge.role === "parent" ? "a_parent" : "siblings"} testid="rel-edit-role"
                onChange={(r) => patch(r === "siblings" ? { role: "siblings" } : { role: "parent", parent_id: r === "a_parent" ? edge.a : edge.b })} />
            </div>
          )}
          <div className="flex gap-2">
            <Input value={status} onChange={(e) => setStatus(e.target.value)} placeholder="Status" className="h-9" data-testid="rel-edit-status-input" />
            <Button size="sm" variant="outline" onClick={() => patch({ status })} disabled={status === (edge.status || "")} data-testid="rel-edit-status-save">Save</Button>
          </div>
        </div>
      ) : edge.status && <p className="text-sm">Status: {edge.status}</p>}
      <div className="mt-4 flex gap-2">
        <Button variant="outline" size="sm" onClick={() => onOpenEntry(edge.id)} data-testid="rel-open-edge-entry"><ExternalLink className="mr-1.5 h-3.5 w-3.5" />Open entry</Button>
        {canEdit && <Button variant="ghost" size="sm" className="text-red-400" onClick={() => onDelete(edge)} data-testid="rel-edge-delete"><Trash2 className="mr-1.5 h-3.5 w-3.5" />Delete</Button>}
      </div>
    </Shell>
  );
}
